import { useEffect, useMemo, useRef, type ChangeEvent } from 'react'
import { Button } from '@/common/ui/Form/Button/Button'
import { Card } from '@/common/ui/Card/Card'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import {
    useCompanyStatsBrandComparison,
    type CompanyStatsComparisonMode,
} from '../../model/useCompanyStatsBrandComparison'
import type { CompanyStatsDataHooks } from '../../model/hooks'
import { CompanyStatsBrandComparisonHeader, type BrandComparisonOption } from './CompanyStatsBrandComparisonHeader'
import { CompanyStatsBrandComparisonSummaryCards } from './CompanyStatsBrandComparisonSummaryCards'
import { CompanyStatsBrandComparisonScoreChart } from './CompanyStatsBrandComparisonScoreChart'
import { CompanyStatsBrandComparisonContentBlocks } from './CompanyStatsBrandComparisonContentBlocks'
import { CompanyStatsBrandComparisonProducts } from './CompanyStatsBrandComparisonProducts'
import { CompanyStatsBrandComparisonCriteriaTables } from './CompanyStatsBrandComparisonCriteriaTables'

type CompanyStatsBrandComparisonDashboardProps = {
    orgId: string
    compareBy: CompanyStatsComparisonMode
    entityAId: string
    entityBId: string
    entityOptions: BrandComparisonOption[]
    onCompareByChange: (e: ChangeEvent<HTMLSelectElement>) => void
    onEntityAChange: (e: ChangeEvent<HTMLSelectElement>) => void
    onEntityBChange: (e: ChangeEvent<HTMLSelectElement>) => void
    hooks?: Pick<CompanyStatsDataHooks, 'useCompanyStatsCommon' | 'useCompanyStatsScores' | 'useCompanyStatsProducts'>
}

export const CompanyStatsBrandComparisonDashboard = ({
    orgId,
    compareBy,
    entityAId,
    entityBId,
    entityOptions,
    onCompareByChange,
    onEntityAChange,
    onEntityBChange,
    hooks,
}: CompanyStatsBrandComparisonDashboardProps) => {
    const toast = useOptionalToast()
    const lastErrorMessageRef = useRef<string | null>(null)
    const entityNames = useMemo(
        () => Object.fromEntries(entityOptions.map(entity => [entity.id, entity.name])),
        [entityOptions]
    )

    const enrichedBrandOptions = useMemo(() => {
        const countById = new Map<string, number>()
        return entityOptions.map(entity => ({
            ...entity,
            productCount: countById.get(entity.id),
        }))
    }, [entityOptions])

    const { brandA, brandB, isLoading, error, refetch, isReady } = useCompanyStatsBrandComparison(
        orgId,
        compareBy,
        entityAId,
        entityBId,
        entityNames,
        hooks
    )

    const entityAName = entityNames[entityAId] ?? entityAId
    const entityBName = entityNames[entityBId] ?? entityBId

    const selectOptions = useMemo(() => {
        if (!brandA || !brandB) return enrichedBrandOptions
        return entityOptions.map(entity => ({
            ...entity,
            productCount:
                entity.id === brandA.id
                    ? brandA.common.total_products
                    : entity.id === brandB.id
                      ? brandB.common.total_products
                      : undefined,
        }))
    }, [brandA, brandB, entityOptions, enrichedBrandOptions])

    const entityLabel = compareBy === 'brand' ? 'бренда' : 'продавца'
    const entityLabelPlural = compareBy === 'brand' ? 'бренда' : 'продавца'
    const hasEnoughOptions = entityOptions.length >= 2

    useEffect(() => {
        if (!error) {
            lastErrorMessageRef.current = null
            return
        }

        if (lastErrorMessageRef.current === error.message) {
            return
        }

        lastErrorMessageRef.current = error.message
        toast?.showToast({
            type: 'error',
            message: `Ошибка загрузки: ${error.message}`,
        })
    }, [error, toast])

    if (!hasEnoughOptions || !isReady) {
        return (
            <div className='flex flex-col gap-6'>
                <CompanyStatsBrandComparisonHeader
                    compareBy={compareBy}
                    entityAId={entityAId}
                    entityBId={entityBId}
                    entityAName={entityAName}
                    entityBName={entityBName}
                    entityOptions={selectOptions}
                    onCompareByChange={onCompareByChange}
                    onEntityAChange={onEntityAChange}
                    onEntityBChange={onEntityBChange}
                />
                <Card color='primary'>
                    <div className='flex min-h-24 items-center justify-center px-4 py-6 text-center text-sm  text-base-content/60'>
                        {hasEnoughOptions
                            ? `Выберите два разных ${entityLabel} для сравнения`
                            : `Для сравнения нужно минимум два ${entityLabelPlural}`}
                    </div>
                </Card>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className='flex flex-col gap-6'>
                <CompanyStatsBrandComparisonHeader
                    compareBy={compareBy}
                    entityAId={entityAId}
                    entityBId={entityBId}
                    entityAName={entityAName}
                    entityBName={entityBName}
                    entityOptions={selectOptions}
                    onCompareByChange={onCompareByChange}
                    onEntityAChange={onEntityAChange}
                    onEntityBChange={onEntityBChange}
                />
                <div className='flex items-center justify-center py-16'>
                    <span className='loading loading-spinner loading-lg' aria-label='Загрузка сравнения' />
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className='flex flex-col gap-6'>
                <CompanyStatsBrandComparisonHeader
                    compareBy={compareBy}
                    entityAId={entityAId}
                    entityBId={entityBId}
                    entityAName={entityAName}
                    entityBName={entityBName}
                    entityOptions={selectOptions}
                    onCompareByChange={onCompareByChange}
                    onEntityAChange={onEntityAChange}
                    onEntityBChange={onEntityBChange}
                />
                <Button type='button' emphasis='accent' onClick={refetch}>
                    Обновить страницу
                </Button>
            </div>
        )
    }

    if (!brandA || !brandB) {
        return null
    }

    return (
        <div className='flex min-w-0 flex-col gap-6'>
            <CompanyStatsBrandComparisonHeader
                compareBy={compareBy}
                entityAId={entityAId}
                entityBId={entityBId}
                entityAName={brandA.name}
                entityBName={brandB.name}
                entityOptions={selectOptions}
                onCompareByChange={onCompareByChange}
                onEntityAChange={onEntityAChange}
                onEntityBChange={onEntityBChange}
            />

            <CompanyStatsBrandComparisonSummaryCards brandA={brandA} brandB={brandB} />

            <div className='grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2'>
                <CompanyStatsBrandComparisonScoreChart brandA={brandA} brandB={brandB} />
                <CompanyStatsBrandComparisonContentBlocks brandA={brandA} brandB={brandB} />
            </div>

            <CompanyStatsBrandComparisonProducts brandA={brandA} brandB={brandB} />

            <CompanyStatsBrandComparisonCriteriaTables brandA={brandA} brandB={brandB} />
        </div>
    )
}
